import React, {Fragment} from 'react';
import {DataTablePage} from '@common/datatable/page/data-table-page';
import {IconButton} from '@common/ui/buttons/icon-button';
import {EditIcon} from '@common/icons/material/Edit';
import {FormattedDate} from '@common/i18n/formatted-date';
import {ColumnConfig} from '@common/datatable/column-config';
import {Trans} from '@common/i18n/trans';
import {DataTableEmptyStateMessage} from '@common/datatable/page/data-table-emty-state-message';
import softwareEngineerSvg from '@common/admin/tags/software-engineer.svg';
import {DataTableAddItemButton} from '@common/datatable/data-table-add-item-button';
import {Chat} from './chat';
import {Link} from 'react-router-dom';
import {Tooltip} from '@common/ui/tooltip/tooltip';
import {useDeleteChat} from '@app_modules/admin/chats/requests/use-delete-chat';
import {DeleteIcon} from '@common/icons/material/Delete';
import {DialogTrigger} from '@common/ui/overlays/dialog/dialog-trigger';
import {ConfirmationDialog} from '@common/ui/overlays/dialog/confirmation-dialog';
import {useNavigate} from '@common/utils/hooks/use-navigate';
import {ChatsIndexPageFilters} from './chats-index-page-filters';
import { VisibilityIcon } from '@common/icons/material/Visibility';
import moment from 'moment-timezone';
import { useUserTimezone } from '@common/i18n/use-user-timezone';

export function ChatsIndexPage() {
  const navigate = useNavigate();
  const timezone = useUserTimezone();

  const columnConfig: ColumnConfig<Chat>[] = [
    {
      key: 'id',
      allowsSorting: false,
      maxWidth: 'max-w-100',
      header: () => <Trans message="#" />,
      body:  chat => chat.id,
    },
    {
      key: 'name',
      allowsSorting: false,
      header: () => <Trans message="Chat Name" />,
      body: chat => chat.name
      
    },

    {
      key: 'updated_by',
      allowsSorting: false,
      header: () => <Trans message="Modified By" />,
      body: chat => chat.updated_by
      
    },
    {
      key: 'updated_at',
      allowsSorting: false,
      maxWidth: 'max-w-140',
      header: () => <Trans message="Last Modify Date" />,
      body: chat => chat.updated_at ? moment.utc(chat.updated_at).tz(timezone).format('MM/DD/YYYY HH:mm:ss') :''
      
    },
    {
      key: 'actions',
      header: () => <Trans message="Actions" />,
      visibleInMode: 'all',
      hideHeader: true,
      align: 'end',
      maxWidth: 'max-w-94',
      body:  chat => {
        return (
          <Fragment>
            <IconButton
              size="md"
              className="text-muted"
              elementType={Link}
              to={`/chats/${ chat.id}/view`}
            >
              <VisibilityIcon />
            </IconButton>
            

            <IconButton
              size="md"
              className="text-muted"
              elementType={Link}
              to={`/chats/${ chat.id}/edit`}
            >
              <EditIcon />
            </IconButton>

            <DeleteChatButton  chat={ chat} />
          </Fragment>
        );
      },
    },
  ];

  return (
    <DataTablePage
      endpoint="chats"
      title={<Trans message="Chats" />}
      columns={columnConfig}
      actions={<Actions />}
      enableSelection={false}
      //filters={ChatsIndexPageFilters}
      onRowAction={item => {
        navigate(`/chats/${item.id}/edit`);
      }}
      emptyStateMessage={
        <DataTableEmptyStateMessage
          image={softwareEngineerSvg}
          title={<Trans message="No chats have been created yet" />}
          filteringTitle={<Trans message="No matching chats" />}
        />
      }
    />
  );
}

interface DeleteChatButtonProps {
   chat: Chat;
}
function DeleteChatButton({ chat}: DeleteChatButtonProps) {
  const deleteChat = useDeleteChat();
  return (
    <DialogTrigger
      type="modal"
      onClose={confirmed => {
        if (confirmed) {
          deleteChat.mutate({ chatId:  chat.id});
        }
      }}
    >
      <Tooltip label={<Trans message="Delete chat" />}>
        <IconButton
          size="md"
          className="text-muted"
          disabled={deleteChat.isPending}
        >
          <DeleteIcon />
        </IconButton>
      </Tooltip>
      <ConfirmationDialog
        title={<Trans message="Delete chat" />}
        body={<Trans message="Are you sure you want to delete this chat?" />}
        confirm={<Trans message="Delete" />}
      />
    </DialogTrigger>
  );
}

function Actions() {
  return (
    <Fragment>
      <DataTableAddItemButton elementType={Link} to="/chats/new">
        <Trans message="Add new chat" />
      </DataTableAddItemButton>
    </Fragment>
  );
}
